import { api } from '../api';
import type { Allergen, Material, CreateMaterialInput, UpdateMaterialInput, BulkCreateMaterialItem } from '@/types/material';
import type { PaginationParams, PaginatedResponse } from '@/types/common';
import type { DashboardMaterialsStats } from '@/types/company';
import type { IngredientConversion, UpsertIngredientConversionInput } from '@/types/material-conversion';

export interface MaterialListParams extends PaginationParams {
  isActive?: boolean;
  includeInactive?: boolean;
}

export interface BulkImportError {
  index: number;
  name: string;
  error: string;
}

export interface BulkImportResult {
  created: number;
  failed: BulkImportError[];
}

/** `GET /materials/:id/usage` — what deleting the ingredient would touch (T-1401). */
export interface MaterialUsage {
  materialId: number;
  recipes: Array<{ id: number; name: string }>;
  parentMaterials: Array<{ id: number; name: string }>;
}

export interface AddSubIngredientInput {
  childMaterialId?: number;
  childRecipeId?: number;
  quantity: number;
  unit: string;
}

function buildQueryString(params?: MaterialListParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', params.page.toString());
  if (params.limit) sp.set('limit', params.limit.toString());
  if (params.sortBy) sp.set('sortBy', params.sortBy);
  if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
  if (params.search) sp.set('search', params.search);
  if (params.isActive !== undefined) sp.set('isActive', String(params.isActive));
  if (params.includeInactive !== undefined) sp.set('includeInactive', String(params.includeInactive));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const materialApi = {
  getAll: (params?: MaterialListParams) =>
    api.get<PaginatedResponse<Material>>(`/materials${buildQueryString(params)}`),
  getById: (id: string | number) => api.get<Material>(`/materials/${id}`),
  create: (data: CreateMaterialInput) => api.post<Material>('/materials', data),
  bulkCreate: (items: BulkCreateMaterialItem[], organizationId?: number, dryRun?: boolean) =>
    api.post<BulkImportResult>('/materials/bulk-create', {
      items,
      organizationId,
      // Validate only: the API runs every per-row check and reports failures
      // without creating anything — the import wizard's preview step.
      ...(dryRun && { dryRun: true }),
    }),
  update: (id: string | number, data: UpdateMaterialInput) => api.patch<Material>(`/materials/${id}`, data),
  // A soft delete: the ingredient and the recipe lines naming it are hidden, not erased.
  // skipToast on both: the delete dialog shows the server's message itself
  // (getApiErrorMessage), so the client's auto-toast would double it.
  delete: (id: string | number) =>
    api.delete<{ id: number; recipeLinesDeleted: number }>(`/materials/${id}`, { skipToast: true }),
  getUsage: (id: string | number) =>
    api.get<MaterialUsage>(`/materials/${id}/usage`, { skipToast: true }),
  getStats: () => api.get<DashboardMaterialsStats>(`/materials/stats`),
  getScopedStats: (filters: { organizationId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.organizationId !== undefined) params.set('organizationId', String(filters.organizationId));
    const query = params.toString();
    return api.get<DashboardMaterialsStats>(`/materials/stats${query ? `?${query}` : ''}`);
  },
  getAllergens: () => api.get<Allergen[]>('/materials/allergens'),
  createAllergen: (name: string) => api.post<Allergen>('/materials/allergens', { name }),
  addSubIngredient: (parentId: number, data: AddSubIngredientInput) =>
    api.post(`/materials/${parentId}/sub-ingredients`, data),
  removeSubIngredient: (parentId: number, childId: number) =>
    api.delete(`/materials/${parentId}/sub-ingredients/${childId}`),

  // ── Conversions: per-supplier pack-size breakdowns ("UoM & pack size") ────

  getConversions: (materialId: number) =>
    api.get<IngredientConversion[]>(`/materials/${materialId}/conversions`),
  createConversion: (materialId: number, data: UpsertIngredientConversionInput) =>
    api.post<IngredientConversion>(`/materials/${materialId}/conversions`, data),
  updateConversion: (materialId: number, conversionId: number, data: UpsertIngredientConversionInput) =>
    api.patch<IngredientConversion>(`/materials/${materialId}/conversions/${conversionId}`, data),
  deleteConversion: (materialId: number, conversionId: number) =>
    api.delete<{ id: number }>(`/materials/${materialId}/conversions/${conversionId}`),
};
